import {
    FRAGMENT_ANNOTATION_BODY,
    INLINE_ANNOTATION,
    INLINE_BUTTON,
    INLINE_EMOJI,
    INLINE_EXPRESSION,
    INLINE_ICON,
    INLINE_IMAGE,
    INLINE_LINK,
    INLINE_MATH,
    INLINE_MENTION,
    type InlineExpressionData,
} from '@gitbook/doc-core';
import type { Inline } from '@gitbook/slate';
import { emojisByCode, getEmojiForCode } from '@gitbook/util-emojis';

import { footnoteIdToHtmlId } from '../../utils';
import { createImage } from './transform-blocks';
import type { GastToHastTransformMap } from './types';
import { createCustomInline } from './utils';

export const transformInlines: GastToHastTransformMap<Inline> = {
    [INLINE_BUTTON]: async (node, _eat, options) => {
        const resolved = await options.resolveContentRef(node.data.get('ref'));
        if (!resolved) {
            return [];
        }

        const icon = node.data.get('icon');

        return [
            {
                type: 'element' as const,
                tagName: 'a',
                properties: {
                    href: resolved.url,
                    className: ['button', node.data.get('kind')],
                    ...(typeof icon === 'string'
                        ? {
                              'data-icon': icon,
                          }
                        : {}),
                },
                children: [
                    {
                        type: 'text' as const,
                        value: node.data.get('label'),
                    },
                ],
            },
        ];
    },
    [INLINE_LINK]: async (node, eat, options) => {
        const resolved = await options.resolveContentRef(node.data.get('ref'));
        if (!resolved) {
            return [];
        }

        return [
            {
                type: 'element',
                tagName: 'a',
                properties: {
                    title: node.data.get('title') || null,
                    href: resolved.url,
                },
                children: await eat(node.nodes),
            },
        ];
    },
    [INLINE_MATH]: (node) => {
        const formula = node.data.get('formula');

        return [
            {
                type: 'element',
                tagName: 'span',
                properties: {
                    className: ['math'],
                },
                children: [
                    {
                        type: 'text',
                        value: formula,
                    },
                ],
            },
        ];
    },
    [INLINE_MENTION]: async (node, _eat, options) => {
        const ref = node.data.get('ref');
        const resolved = await options.resolveContentRef(ref);

        if (!resolved) {
            return [];
        }

        const properties = {
            'data-mention': true,
            title: null,
            href: resolved.url,
        };

        return [
            {
                type: 'element',
                tagName: 'a',
                properties,
                children: [
                    {
                        type: 'text',
                        value: resolved.text,
                    },
                ],
            },
        ];
    },
    [INLINE_EMOJI]: (node) => {
        const code = node.data.get('code');
        const emoji = emojisByCode[code];
        if (!emoji) {
            return [];
        }

        return [
            createCustomInline(
                INLINE_EMOJI,
                {
                    code,
                },
                [
                    {
                        type: 'text',
                        value: getEmojiForCode(emoji.code),
                    },
                ]
            ),
        ];
    },
    [INLINE_ICON]: (node) => {
        const iconName = node.data.get('icon');
        if (typeof iconName !== 'string') {
            return [];
        }

        return [
            {
                type: 'element',
                tagName: 'i',
                properties: {
                    className: [`fa-${iconName}`],
                },
                children: [
                    // We put the icon name in the text to make it easier to read on GitHub
                    // and also to avoid rehype-minify-whitespace from trimming whitespace around it.
                    {
                        type: 'text',
                        value: `:${iconName}:`,
                    },
                ],
            },
        ];
    },
    [INLINE_EXPRESSION]: (node) => {
        const { expression } = node.data.toFrozenObject() as InlineExpressionData;
        return [
            {
                type: 'element',
                tagName: 'code',
                properties: {
                    className: ['expression'],
                },
                children: [
                    {
                        type: 'text',
                        value: expression,
                    },
                ],
            },
        ];
    },
    [INLINE_IMAGE]: async (node, _eat, options) => {
        return createImage(node, () => Promise.resolve([]), options);
    },
    [INLINE_ANNOTATION]: async (node, eat, context) => {
        const fragment = node.getFragmentByKey(FRAGMENT_ANNOTATION_BODY);
        const children = await eat(node.nodes);

        if (!fragment) {
            return children;
        }

        const footnoteId = context.allocateFootnote(fragment);

        return [
            {
                type: 'element',
                tagName: 'a',
                properties: {
                    dataFootnoteRef: true,
                    href: `#${footnoteIdToHtmlId(footnoteId)}`,
                },
                children,
            },
        ];
    },
};
